<template>
  <a
    class="jiffy-service-preview"
    :data-mixpanel-listen="mixpanelListeners"
    :data-mixpanel-event="mixpanelEvents"
    :data-mixpanel-data="mixpanelData"
    :class="wrapperClassNames"
    :href="service.page_url"
    :target="openInSameTab ? '_self' : '_blank'"
  >
    <img
      :src="service.preview"
      alt="Service Preview"
      class="jiffy-service-preview__preview"
      data-testid="preview"
    />

    <UseSvg :icon="service.icon" class="jiffy-service-preview__icon" />

    <div class="jiffy-service-preview__title" data-testid="title">
      {{ service.title }}
    </div>

    <div v-if="showProduct" data-testid="product-info" class="jiffy-service-preview__product">
      <div v-if="service.brand_logo" class="jiffy-service-preview__product-row">
        <img
          :src="service.brand_logo"
          alt="Brand Logo"
          class="jiffy-service-preview__brand-logo"
          data-testid="brand-logo"
        />
        <span>|</span>
        <div data-testid="catalog_number" class="jiffy-service-preview__catalog_number">
          {{ service.catalog_number }}
        </div>
      </div>
      <div v-if="service.avg_rating" class="jiffy-service-preview__product-row">
        <Stars :stars="service.avg_rating" size="XS"></Stars>

        <div data-testid="reviews_count" class="jiffy-service-preview__reviews_count">
          {{ reviewsCount }}
        </div>
      </div>
    </div>

    <div class="jiffy-service-preview__prices">
      <div v-if="showFromPrice" class="jiffy-service-preview__price-from">
        starting {{ isFromPriceZero ? "at" : "from" }}
        <s v-if="!isFromPriceZero" data-testid="price-from">{{ service.price_from }}</s>
      </div>

      <div class="jiffy-service-preview__price-per">
        <div class="jiffy-service-preview__price-per-amount" data-testid="price-per">
          {{ service.price_per }}
        </div>
        <div
          v-if="service.price_per_label"
          class="jiffy-service-preview__price-per-label"
          data-testid="price-label"
        >
          {{ service.price_per_label }}
        </div>
      </div>
    </div>

    <div class="jiffy-service-preview__cta" data-testid="cta">
      {{ service.cta }}
    </div>
  </a>
</template>

<script>
import UseSvg from "common/vue/components/UseSvg.vue";
import Stars from "common/vue/components/Stars.vue";

export default {
  name: "JiffyServicePreview",
  components: { UseSvg, Stars },
  props: {
    service: {
      type: Object,
      required: true
    },
    design: {
      type: String,
      required: true
    },
    horizontal: {
      type: Boolean,
      default: false
    },
    mixpanelEvent: {
      type: String,
      required: true
    },
    source: {
      type: String,
      required: false
    },
    showFromPrice: {
      type: Boolean,
      default: true
    },
    showProduct: {
      type: Boolean,
      default: false
    },
    openInNewTab: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    mixpanelEvents() {
      let result = this.mixpanelEvent;
      if (this.service.additional_mixpanel_event) {
        result = [result, this.service.additional_mixpanel_event].join("|");
      }
      return result;
    },
    mixpanelListeners() {
      if (this.service.additional_mixpanel_event) {
        return "click|click";
      }
      return "click";
    },
    mixpanelData() {
      let data = JSON.stringify({
        service: this.service.service,
        catalog_number: this.service.catalog_number,
        page: "cart",
        business_line_promo_type: this.service.business_line_promo_type,
        promotion_name: this.service.promotion_name
      });
      if (this.source) {
        data = [
          data,
          JSON.stringify({
            source: this.source,
            service: this.service.service,
            page: "cart",
            business_line_promo_type: this.service.business_line_promo_type,
            promotion_name: this.service.promotion_name
          })
        ].join("|");
      }
      return data;
    },
    wrapperClassNames() {
      return {
        [`jiffy-service-preview--${this.service.service}`]: true,
        [`jiffy-service-preview--design-${this.design}`]: !this.horizontal,
        "jiffy-service-preview--horizontal": this.horizontal
      };
    },
    isFromPriceZero() {
      return this.service.price_from === "$0";
    },
    isMobileApp() {
      return Boolean(window.MOBILE_APP);
    },
    reviewsCount() {
      return this.service.reviews_count.toString().length > 5
        ? `${this.service.reviews_count / 1000}k+`
        : this.service.reviews_count;
    },
    openInSameTab() {
      return this.isMobileApp || !this.openInNewTab;
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/button/mixin.scss";

.jiffy-service-preview {
  $this: &;

  display: grid;
  grid-template-areas: "preview . icon icon icon" "preview . title title title" "preview . prices cta cta";
  grid-template-columns: min-content 16px auto auto auto;
  align-items: center;
  border: 1px solid $gray-border;
  border-radius: 8px;
  padding: 8px;
  background: $white;
  white-space: nowrap;
  min-height: 118px;
  width: max-content;

  &,
  &:hover,
  &:focus {
    text-decoration: none;
    color: inherit;
  }

  &--transfers {
    #{$this}__icon {
      width: 174px;
    }
  }

  &--designs {
    #{$this}__icon {
      width: 141px;
    }
  }

  &--stickers {
    #{$this}__icon {
      width: 146px;
    }
  }

  &--blanks {
    #{$this}__icon {
      width: 127px;
    }
  }

  &--made {
    #{$this}__icon {
      width: 90px;
    }
  }

  &--design-large {
    grid-template-areas:
      "preview preview preview"
      "icon icon icon"
      "title title title"
      "product product product"
      "prices prices prices"
      "cta cta cta";
    grid-template-columns: auto auto auto;
    width: 50%;
    gap: 5px;

    #{$this}__preview {
      margin: 0 auto;
      height: 142px;
      width: auto;

      @include with-desktop {
        height: 135px;
      }
    }

    #{$this}__title {
      white-space: pre-wrap;
      font-size: 13px;
      line-height: 16px;
    }

    #{$this}__icon {
      height: 18px;
      margin-left: -3px;
      margin-top: 4px;
    }

    #{$this}__price-per-amount {
      font-size: 20px;
    }

    #{$this}__cta {
      margin-left: 0;
      margin-top: 3px;
    }

    @include with-tablet {
      grid-template-areas:
        "preview preview preview"
        "icon icon icon"
        "title title title"
        "product product prices"
        "cta cta cta";

      #{$this}__prices {
        justify-self: flex-end;
      }
    }

    @include with-desktop {
      grid-template-areas:
        "preview icon icon icon"
        "preview title title title"
        "preview product product prices"
        "preview cta cta cta";
      grid-template-columns: min-content auto auto auto;
    }
  }

  &--design-pdp {
    grid-template-columns: min-content 8px auto auto auto;
    min-height: 111px;

    #{$this}__preview {
      display: none;

      @include with-tablet {
        display: block;
        width: 93px;
        height: 93px;
      }
    }

    #{$this}__cta {
      padding-left: 8px;
      padding-right: 8px;
    }
  }

  &--design-cart-variant-1 {
    grid-template-columns: min-content min-content auto auto auto;

    @include with-tablet {
      grid-template-columns: min-content 16px auto auto auto;
    }

    #{$this}__preview {
      display: none;

      @include with-tablet {
        display: block;
      }
    }
  }

  &--design-cart-variant-non-dtf-catalog-products {
    grid-template-areas: "preview . icon" "preview . title" "preview . cta";
    grid-template-columns: min-content 16px auto;

    @include with-tablet {
      grid-template-areas: "preview . icon icon icon" "preview . title title title" "preview . prices . cta";
      grid-template-columns: min-content 16px auto auto auto;
    }

    #{$this}__prices {
      display: none;

      @include with-tablet {
        display: block;
      }
    }

    #{$this}__cta {
      margin-left: 0;

      @include with-tablet {
        margin-left: 8px;
      }
    }
  }

  &--horizontal {
    @include with-tablet {
      grid-template-areas:
        "preview . . . ."
        "preview . icon prices cta"
        "preview . title prices cta"
        "preview . . . .";
      grid-template-columns: min-content 16px auto auto auto;
      grid-template-rows: 1fr auto auto 1fr;
      gap: 5px;
    }
  }

  &__product {
    grid-area: product;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 3px;
  }
  &__product-row {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    width: 100%;
    font-size: 12px;
    font-weight: 700;
  }
  &__brand-logo {
    width: auto;
    height: 10px;
    max-width: 80px;
  }
  &__catalog_number {
    font-size: 12px;
    font-weight: 700;
    color: $text-black;
  }
  &__reviews_count {
    font-size: 12px;
    font-weight: 700;
    color: $deep-blue;
  }
  &__heading {
    grid-area: heading;
    font-size: 18px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;
    margin-top: 8px;
    color: $text-black;
  }
  &__tag {
    position: absolute;
    height: 22px;
    padding: 3px 8px;
    gap: 8px;
    border-radius: 4px;
    color: $white;
    font-size: 12px;
    font-weight: 700;
    background-color: $dark-red;
    left: 15px;
    top: 215px;
  }

  &__preview {
    grid-area: preview;
    border-radius: 4px;
    width: auto;
    height: 100px;
  }

  &__icon {
    grid-area: icon;
    height: 19px;
  }

  &__title {
    grid-area: title;
    font-size: 14px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 0.019px;
  }

  &__prices {
    grid-area: prices;
  }

  &__price-from {
    color: $warm-grey;
    font-size: 12px;
    line-height: 16px;
  }

  &__price-per {
    display: flex;
    font-weight: bold;
    line-height: 1;
  }

  &__price-per-amount {
    color: $price-product-card;
    font-size: 21px;
    margin-right: 4px;
  }

  &__price-per-label {
    font-size: 12px;
    line-height: 0.9;
    white-space: pre;
  }

  &__cta {
    @include jiffy-button;

    grid-area: cta;
    margin-left: 8px;
    border-color: $primary-light;
    border-radius: 8px;
    background-color: $primary-light;
    color: $white;
    font-family: $font-family-inter;
    font-weight: 600;

    &:hover,
    &:focus {
      background-color: $primary;
      border-color: $primary;
      color: $white;
    }
  }
}
</style>
